<script setup lang="ts">
import {
    BBadge, BListGroup, BListItem, BPrimaryMenuIcon,
} from '@/design-system';
import type { NavigationItemType } from '@/types/NavigationItemType';
import { NavigationKeyEnum } from '@/types/enums/NavigationKeyEnum';
import { useNavigationActivityStore } from '@/stores/navigationActivityStore';
import type { RouteLocationRaw } from 'vue-router';

type PropsType = {
    items: NavigationItemType[];
    rail: boolean;
    currentNavigationKey?: string;
};

const props = defineProps<PropsType>();

const emit = defineEmits<{
    expandRail: [];
}>();

const navigationActivityStore = useNavigationActivityStore();

const getRouteTo = (to: string | RouteLocationRaw) => {
    if (!to) {
        return undefined;
    }

    return typeof to === 'string' ? { name: to } : to;
};
</script>

<template>
    <template
        v-for="(item, index) in items"
        :key="('key' in item ? item.key : undefined) || `item-${index}`"
    >
        <BListGroup
            v-if="item.children && item.children.length > 0"
            :item="item"
            :rail="rail"
            density="compact"
            @expand-rail="() => emit('expandRail')"
        />
        <BListItem
            v-else
            :to="item.href ? undefined : getRouteTo(item.to)"
            :href="item.href"
            :target="item.href ? '_blank' : undefined"
            :title="item.title"
            :disabled="item.disabled"
            :active="item.navigationKey === props.currentNavigationKey"
            density="compact"
        >
            <template #prepend>
                <BPrimaryMenuIcon
                    v-if="item.icon"
                    :icon="item.icon"
                    :icon-component="item.iconComponent"
                    :icon-component-width="item.iconComponentWidth"
                    :icon-component-height="item.iconComponentHeight"
                    :icon-component-color="item.iconComponentColor"
                />
            </template>
            <template
                v-if="item.navigationKey && navigationActivityStore.hasDot(item.navigationKey as NavigationKeyEnum)"
                #append
            >
                <span
                    v-if="navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.text"
                    class="navigation-activity-text"
                    :style="{
                        borderColor: `rgb(var(--v-theme-${navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.color ?? 'error'}))`,
                    }"
                >
                    {{ navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.text }}
                </span>
                <BBadge
                    v-else
                    dot
                    :color="
                        navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.color ?? 'ai-red'
                    "
                    inline
                    width="5"
                    height="5"
                    class="badge--navigation-activity"
                />
            </template>
        </BListItem>
    </template>
</template>

<style scoped lang="scss">
:deep(.badge--navigation-activity.v-badge--inline .v-badge__wrapper) {
    display: flex;
    position: relative;
    margin: 0;
    justify-content: start;
    align-items: start;
}

.navigation-activity-text {
    font-size: 0.7em;
    line-height: 1;
    padding: 2px 6px;
    border: 1.5px solid;
    border-radius: 8px;
    white-space: nowrap;
    font-weight: 400;
    color: rgba(var(--v-theme-grey-700));
}
</style>
